import {AfterViewInit, Component, effect, ElementRef, input, SimpleChanges, viewChild} from '@angular/core';
import * as marked from 'marked';
import {markedHighlight} from 'marked-highlight';
import hljs from 'highlight.js';
import 'mathjax/es5/tex-mml-chtml.js';
import {markedHighlight2} from '../lib/marked-code-highlight';

@Component({
    selector: 'app-markdown-renderer',
    standalone: true,
    templateUrl: './markdown-renderer.component.html',
    styleUrls: ['./markdown-renderer.component.scss']
})
export class MarkdownRendererComponent {

    /** Global flag to indicate, if the MaxJax library has been loaded. */
    static isMathJaxLoaded = false;

    public mathJax = input<boolean>(false);
    public markdown = input<string | null | undefined>(null);

    // private readonly sourceEl = viewChild.required<ElementRef<HTMLElement>>('source');
    private readonly targetEl = viewChild<ElementRef<HTMLElement>>('target');

    private markedRenderer: marked.Marked;

    constructor() {
        this.markedRenderer =
            new marked.Marked(markedHighlight2({
                    emptyLangClass: 'hljs',
                    langPrefix: 'hljs language-',
                    highlight(code: any, lang: any, info: any) {
                        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
                        return hljs.highlight(code, { language }).value;
                    }
                }
            ))
        ;

        effect(async () => await this.render());
    }

    async render2(): Promise<void> {
        const targetEl = this.targetEl()?.nativeElement;
        if (targetEl) {
            const markdownCode = this.markdown() ?? '';
            const hasMathJax = this.mathJax() || MarkdownRendererComponent.hasMathJax(markdownCode);

            targetEl.innerHTML = await this.markedRenderer.parse(escapeBackslashBrackets(markdownCode));
            if (hasMathJax) {
                try {
                    await MathJax.typeset([targetEl]);
                } catch (e) {
                    console.error('Error while rendering MathJax', e);
                }
            }
        }
    }

    async render3(): Promise<void> {
        const targetEl = this.targetEl()?.nativeElement;
        if (targetEl) {
            let markdownCode = this.markdown() ?? '';
            const hasMathJax = this.mathJax() || MarkdownRendererComponent.hasMathJax(markdownCode);

            targetEl.innerHTML = markdownCode;
            if (hasMathJax) {
                try {
                    MathJax.typeset([targetEl]);
                    markdownCode = targetEl.innerHTML;
                } catch (e) {
                    console.error('Error while rendering MathJax', e);
                }
            }
            const markdownCodeWithRenderedMathJax = markdownCode;
            targetEl.innerHTML = await this.markedRenderer.parse(markdownCodeWithRenderedMathJax);
        }
    }

    async render(): Promise<void> {
        const targetEl = this.targetEl()?.nativeElement;
        if (targetEl) {
            const markdownCode = this.markdown() ?? '';
            const segments = parseMathSegments(markdownCode);
            const parts: string[] = [];

            for (const seg of segments) {
                if (seg.type === 'text') {
                    parts.push(seg.raw);
                } else {
                    try {
                        const html = MathJax.tex2chtml(seg.content, { display: !!seg.display });
                        parts.push(html.outerHTML);
                    } catch (e) {
                        parts.push(`<code>${escapeHtml(seg.raw)}</code>`);
                    }
                }
            }

            const markdownCodeWithRenderedMathJax = parts.join('');
            targetEl.innerHTML = await this.markedRenderer.parse(markdownCodeWithRenderedMathJax);
            (MathJax as any).startup.document.reset();
            (MathJax as any).startup.document.updateDocument();
        }
    }

    static hasMathJax(text: string): boolean {
        return /\$.*?\$/s.test(text) || /\\\(.*?\\\)/s.test(text) || /\\\[.*?\\\]/s.test(text) ||  /\\\(.*?\\\)/s.test(text);
    }

    async xngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['markdown']) {
            await this.render();
        }
    }
}

const markedRenderer = new marked.Marked(
    markedHighlight({
        emptyLangClass: 'hljs',
        langPrefix: 'hljs language-',
        highlight(code, lang, info) {
            const language = hljs.getLanguage(lang) ? lang : 'plaintext';
            return hljs.highlight(code, { language }).value;
        }
    })
);

function escapeBackslashBrackets(input: string): string {
    return input.replace(/\\([\[\]()])/g, '\\\\$1');
}

type Segment = {
    type: 'text' | 'math';
    raw: string;
    content: string;
    display: boolean;
    delimiter: string;
};

/**
 * Split input into text/math segments (same as before).
 */
function parseMathSegments(input: string): Segment[] {
    if (!input) return [{ type: 'text', raw: '', content: '', display: false, delimiter: '' }];

    const regex = /(\$\$[\s\S]*?\$\$|\$[^$\n][\s\S]*?\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/g;
    const segments: Segment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    // tslint:disable-next-line:no-conditional-assignment
    while ((match = regex.exec(input)) !== null) {
        const idx = match.index;
        if (idx > lastIndex) {
            const slice = input.slice(lastIndex, idx);
            segments.push({ type: 'text', raw: slice, content: slice, display: false, delimiter: '' });
        }
        const raw = match[0];
        let content = raw;
        let display = false;
        let delimiter = '';

        if (raw.startsWith('$$') && raw.endsWith('$$')) {
            content = raw.slice(2, -2);
            display = true;
            delimiter = '$$';
        } else if (raw.startsWith('$') && raw.endsWith('$')) {
            content = raw.slice(1, -1);
            display = false;
            delimiter = '$';
        } else if (raw.startsWith('\\[') && raw.endsWith('\\]')) {
            content = raw.slice(2, -2);
            display = true;
            delimiter = '\\[';
        } else if (raw.startsWith('\\(') && raw.endsWith('\\)')) {
            content = raw.slice(2, -2);
            display = false;
            delimiter = '\\(';
        }

        segments.push({ type: 'math', raw, content, display, delimiter });
        lastIndex = idx + raw.length;
    }

    if (lastIndex < input.length) {
        const content = input.slice(lastIndex);
        segments.push({ type: 'text', raw: content, content, display: false, delimiter: '' });
    }

    return segments;
}

/** simple HTML escaper for text segments */
function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, c =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c]
    );
}
